import React, {useEffect, useMemo, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import toast, {Toaster} from 'react-hot-toast';

import {fetchChangeOptions, matrixSelector, setPreloadedOptions} from "./slices/matrixSlice";
import Layout from './components/Layout';

// The server-rendered placeholder (themes/.../avery_configurator/includes/layout.html.twig)
// has the same outer Layout structure as App.jsx's <Layout> output. Splitting it
// into the two column inner-HTMLs lets the not-ready state render with the
// same React component tree (Layout + two column divs) as the ready state, so
// React reuses the DOM when `ready` flips instead of unmounting+remounting —
// which previously caused ~0.6 CLS on print-service pages with the configurator.
const splitPlaceholder = (placeholder) => {
  if (!placeholder || typeof document === 'undefined') return { col1: '', col2: '' };
  const tmp = document.createElement('div');
  tmp.innerHTML = placeholder;
  const root = tmp.querySelector('.placeholder');
  if (!root || root.children.length < 2) return { col1: '', col2: '' };
  return {
    col1: root.children[0].innerHTML,
    col2: root.children[1].innerHTML,
  };
};

const InitialStateLoader = ({children, defaultConfig, productId, optionsOverwrite, pageOptionsHide, pageOptionsFilter, priceListStyle, placeholder, fetched, denyCustomFormats, preloadedOptions}) => {
  const {
    isError,
    errorMessage,
    isSuccess,
    AveryCustom: { client, customVat },
  } = useSelector(matrixSelector);
  const dispatch = useDispatch();
  // If the store was pre-populated (e.g. via dispatch in index.js), isSuccess
  // is already true — skip the placeholder and render children immediately.
  const [ready, setReady] = useState(fetched || isSuccess);
  const placeholderCols = useMemo(() => splitPlaceholder(placeholder), [placeholder]);

  useEffect(() => {
    const isAu = window.location.hostname.endsWith('.au');
    const customQuantities = [];
    const urlParams = new URLSearchParams(window.location.search);
    const cqParam = urlParams.get('cq');

    const cwParam = urlParams.get('cw');
    const chParam = urlParams.get('ch');

    let sParam = urlParams.get('s');

    const matParam = urlParams.get('mat');

    const finParam = urlParams.get('fin');

    const sizeParam = urlParams.get('size');

    if (cqParam !== null) {
      const cqValue = parseInt(cqParam, 10);
      if (!isNaN(cqValue)) {
        customQuantities.push(cqValue);
        optionsOverwrite = {...optionsOverwrite, 'quantity': `custom_qty_${cqValue}`};
      }
    }

    if (cwParam !== null && chParam !== null) {
      const cwValue = parseInt(cwParam, 10);
      const chValue = parseInt(chParam, 10);
      if (!isNaN(cwValue) && !isNaN(chValue)) {
        optionsOverwrite = {...optionsOverwrite, 'format': `custom_${cwValue}_${chValue}_`};
      }
    }

    if (sParam !== null) {
      if (isAu && !sParam.includes('wpau_')) {
          sParam = 'wpau_' + sParam;
      }
      optionsOverwrite = {...optionsOverwrite, 'shape': `sha_${sParam}`};
    }

    if (matParam !== null && !optionsOverwrite['paper']) {
      optionsOverwrite = {...optionsOverwrite, 'paper': matParam};
    }

    if (finParam !== null) {
      optionsOverwrite = {...optionsOverwrite, 'finishing': finParam};
    }

    if (sizeParam !== null && !optionsOverwrite['format']) {
      // Size IDs from the M2 matrix are country-prefixed (e.g. siz_wpde_60mm).
      // Pass the full token through verbatim — no prefix logic in JS, matches
      // matrix values directly.
      optionsOverwrite = {...optionsOverwrite, 'format': sizeParam};
    }

    if (ready === false) {
      if (preloadedOptions && (!optionsOverwrite || Object.keys(optionsOverwrite).length === 0)) {
        dispatch(
          setPreloadedOptions({
            data: { webProduct: preloadedOptions },
            isInitialRequest: true,
            variables: {
              defaultConfig,
              client,
              customVat,
              productId,
              priceListStyle,
              denyCustomFormats,
              pageOptionsFilter,
              pageOptionsHide,
            },
          }),
        );
        setReady(true);
      } else {
        dispatch(
          fetchChangeOptions({
            variables: {
              productId,
              client,
              customVat,
              defaultConfig,
              customQuantities,
              pageOptionsHide,
              pageOptionsFilter,
              priceListStyle,
              denyCustomFormats,
              optionsOverwrite: optionsOverwrite ? optionsOverwrite : {},
            },
            isInitialRequest: true,
          }),
        ).then(() => {
          setReady(true);
        });
      }
    }
  }, []);

  useEffect(() => {
    if (isError) {
      toast.error(errorMessage);
    }
  }, [isError]);

  return (
    <>
      <Toaster/>
      <Choose>
        <When condition={ready === true && !isError}>
          {children}
        </When>
        <Otherwise>
          <Layout>
            <div className="-mx-md px-md overflow-hidden shrink basis-1/2 box-content laptop:max-w-[520px]"
                 dangerouslySetInnerHTML={{__html: placeholderCols.col1}}/>
            <div className="flex-none basis-1/2"
                 dangerouslySetInnerHTML={{__html: placeholderCols.col2}}/>
          </Layout>
        </Otherwise>
      </Choose>
    </>
  )
};

export default InitialStateLoader;
